import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AGENT_INTERCEPTION_CONFIRM_CONTENT } from '../../constants';
import { AIStatusColors } from '../../redux/features/ai/ai.constants';
import {
  selectAIStatus,
  selectIsAIAutoMode,
  selectIsAIEscalation,
} from '../../redux/features/ai/ai.selector';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * AGENT INTERCEPTION CONFIRM (HITL 3.0).
 *
 * Headless guard for an agent stepping into a live auto-AI order: while the AI is
 * driving smoothly (auto mode + green status + no escalation), the first
 * keydown/mousedown that isn't a chrome interaction (header, account menu,
 * layout-preference dialog, pane resize handles) opens an "are you sure you want
 * to take over?" ConfirmDialog. Once confirmed it stops asking for the rest of
 * this auto-AI window.
 *
 * The legacy layout gets this behaviour from <AIStatusBar/>. The HITL 3.0 layout
 * replaced AIStatusBar with the display-only Pulse status band, so this component
 * re-mounts the same guard in MainPageLayout (which Body renders only under
 * hitl_3_0) — otherwise agents would take over auto-AI orders with no prompt.
 * Kept in lockstep with AIStatusBar's dialog logic.
 */
interface IInterceptionConfirmState {
  open: boolean;
  confirm: boolean;
}

// Interactions that are NOT "taking over the order": the header, the account
// menu, the Layout-preference dialog, and the pane resize handles (some render in
// portals, outside the header DOM). Mirrors AIStatusBar.
const IGNORED_SELECTORS = [
  '[data-header="true"]',
  '[data-profile-menu="true"]',
  '[data-layout-preference="true"]',
  '[data-resize-handle]',
];

const AgentInterceptionConfirm = () => {
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isAIEscalation = useSelector(selectIsAIEscalation);
  const aiStatus = useSelector(selectAIStatus);

  const confirmAgentInterception =
    isAIAutoMode && aiStatus.status === AIStatusColors.green && !isAIEscalation;
  const [dialog, setDialog] = useState<IInterceptionConfirmState>({
    open: false,
    confirm: false,
  });

  useEffect(() => {
    if (!confirmAgentInterception || dialog.confirm) {
      return undefined;
    }
    const handleKeyboardMouseEvent = (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target &&
        typeof target.closest === 'function' &&
        IGNORED_SELECTORS.some((selector) => target.closest(selector))
      ) {
        return;
      }
      setDialog((prevState) => ({ ...prevState, open: true }));
    };
    document.addEventListener('keydown', handleKeyboardMouseEvent, true);
    document.addEventListener('mousedown', handleKeyboardMouseEvent, true);
    return () => {
      document.removeEventListener('keydown', handleKeyboardMouseEvent, true);
      document.removeEventListener('mousedown', handleKeyboardMouseEvent, true);
    };
  }, [confirmAgentInterception, dialog]);

  return (
    <ConfirmDialog
      open={dialog.open}
      content={AGENT_INTERCEPTION_CONFIRM_CONTENT}
      onClose={() => setDialog((prevState) => ({ ...prevState, open: false }))}
      onConfirm={() => setDialog({ open: false, confirm: true })}
      closeBtnText={'No'}
      confirmBtnText={'Yes'}
    />
  );
};

export default AgentInterceptionConfirm;
